import { useEffect, useMemo, useState, Fragment, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, ShoppingCart, X } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { listCatalogue, fetchCatalogueCategories, fetchCatalogueBrands, fetchCatalogueCollections, type CatalogueRow } from '@/api/catalogue';
import { formatINRSymbol } from '@/lib/format';
import { useCart } from '@/cart/useCart';
import { AddToCartPopup } from './AddToCartPopup';
import { CartDrawer } from './CartDrawer';

const STOCK_LABEL = { GREEN: 'In Stock', AMBER: 'Low', RED: 'Out' } as const;

/**
 * Render a product name so it wraps only at word/segment boundaries — a <wbr>
 * (zero-width break opportunity) after each underscore and space — instead of
 * chopping mid-segment. Keeps a size like "120X170CM" whole and lets a long
 * code break as "PLT_101_GREY_" / "340×240cm".
 */
function wrappableName(name: string): ReactNode {
  const tokens = name.split(/([_\s])/); // keep the _ / space delimiters
  return tokens.map((t, i) =>
    t === '_' || /\s/.test(t)
      ? <Fragment key={i}>{t}<wbr /></Fragment>
      : <Fragment key={i}>{t}</Fragment>,
  );
}

const PAGE_SIZE = 500;

export function CataloguePage() {
  const cart = useCart();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [brand, setBrand] = useState('');
  const [collection, setCollection] = useState('');
  const [inStock, setInStock] = useState(false);
  // Pricing tier is hidden in the catalogue UI — defaults to MRP.
  const [customerType] = useState('');
  const [popupItem, setPopupItem] = useState<CatalogueRow | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [preview, setPreview] = useState<{ url: string; label: string } | null>(null);
  // How many items to load while browsing; grows by PAGE_SIZE on "Load more".
  const [limit, setLimit] = useState(PAGE_SIZE);

  const isSearching = search.trim().length > 0;

  // Any filter change resets the page back to the first PAGE_SIZE.
  useEffect(() => { setLimit(PAGE_SIZE); }, [search, category, brand, collection, inStock, customerType]);

  const filter = useMemo(
    () => ({
      search: search || undefined,
      category: category || undefined,
      brand: brand || undefined,
      collection: collection || undefined,
      inStock: inStock || undefined,
      customerType: customerType || undefined,
      // Fetch every match immediately when searching OR when the "In stock only"
      // filter is on (so the in-stock view is never capped by the browse page
      // size). Otherwise page through with a growing limit.
      ...(isSearching || inStock ? { full: true } : { limit }),
    }),
    [search, category, brand, collection, inStock, customerType, isSearching, limit],
  );

  const query = useQuery({
    queryKey: ['catalogue', filter],
    queryFn: () => listCatalogue(filter),
    staleTime: 30_000,
    // "Load more" only raises the limit: keep the items already shown on screen while
    // the bigger page loads, so the grid (and the scroll position) stays put and the new
    // items appear below. Any other filter change starts fresh.
    placeholderData: (prev, prevQuery) => {
      const p = prevQuery?.queryKey[1] as Record<string, unknown> | undefined;
      const cur = filter as Record<string, unknown>;
      if (p?.limit == null || cur.limit == null) return undefined;
      const sameFilters = (o: Record<string, unknown>) => JSON.stringify({ ...o, limit: undefined });
      return sameFilters(p) === sameFilters(cur) ? prev : undefined;
    },
  });
  const rows = query.data ?? [];

  const categoriesQuery = useQuery({
    queryKey: ['catalogue-categories'],
    queryFn: fetchCatalogueCategories,
    staleTime: 0, // always reflect the live Item Master
    refetchOnWindowFocus: true,
  });

  const brandsQuery = useQuery({
    queryKey: ['catalogue-brands'],
    queryFn: fetchCatalogueBrands,
    staleTime: 60_000,
  });

  const collectionsQuery = useQuery({
    queryKey: ['catalogue-collections'],
    queryFn: fetchCatalogueCollections,
    staleTime: 60_000,
  });
  const collectionOptions = useMemo(() => [
    { value: '', label: 'All collections' },
    ...(collectionsQuery.data ?? []).map((c) => ({ value: c.name, label: c.name, sublabel: `${c.items} item${c.items === 1 ? '' : 's'}` })),
  ], [collectionsQuery.data]);

  // Unfiltered query (no brand/search/category) solely used to determine which
  // categories have live stock — so the dropdown order stays stable regardless
  // of active filters.
  const allStockQuery = useQuery({
    queryKey: ['catalogue', 'all-for-stock'],
    queryFn: () => listCatalogue({}),
    staleTime: 60_000,
  });

  // Only categories that actually exist on items in the Item Master (no
  // master-list-only values), sorted by stock availability (in-stock first),
  // alpha within groups.
  const categories = useMemo(() => {
    const all = Array.from(new Set(categoriesQuery.data ?? []));

    // Build a set of categories that have at least one in-stock item
    const inStockCats = new Set<string>();
    for (const r of allStockQuery.data ?? []) {
      if (r.stockStatus !== 'RED') inStockCats.add(r.category);
    }

    return all.sort((a, b) => {
      const aIn = inStockCats.has(a) ? 0 : 1;
      const bIn = inStockCats.has(b) ? 0 : 1;
      if (aIn !== bIn) return aIn - bIn;
      return a.localeCompare(b);
    });
  }, [categoriesQuery.data, allStockQuery.data]);

  return (
    // Full-height page: filter + cart chip pin at top, only the grid
    // scrolls. Uses viewport - topbar (64) - main padding (~24) as the
    // outer height budget.
    <div className="flex h-[calc(100vh-72px)] min-h-0 flex-col md:h-[calc(100vh-88px)]">
      <SectionHeader
        title="Product Catalogue"      />

      <FilterBar>
        <FilterField label="Search" className="sm:w-[280px]">
          <div className="relative w-full sm:flex-1">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8"
              aria-label="Search products"
            />
          </div>
        </FilterField>
        <FilterField label="Category" className="sm:min-w-[200px]">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full"
          >
            <option value="">All Categories</option>
            {(() => {
              const allStock = allStockQuery.data ?? [];
              // Count in-stock items per category
              const stockCount = new Map<string, number>();
              for (const r of allStock) {
                if (r.stockStatus !== 'RED') {
                  stockCount.set(r.category, (stockCount.get(r.category) ?? 0) + 1);
                }
              }
              const inStock = categories.filter((c) => (stockCount.get(c) ?? 0) > 0);
              const outOfStock = categories.filter((c) => (stockCount.get(c) ?? 0) === 0);
              return (
                <>
                  {inStock.map((c) => (
                    <option key={c} value={c}>
                      {c} ({stockCount.get(c)} in stock)
                    </option>
                  ))}
                  {outOfStock.length > 0 && inStock.length > 0 && (
                    <option disabled>──── Out of Stock ────</option>
                  )}
                  {outOfStock.map((c) => (
                    <option key={c} value={c}>{c} (0 in stock)</option>
                  ))}
                </>
              );
            })()}
          </Select>
        </FilterField>
        <FilterField label="Collection" className="sm:w-[210px]">
          <SearchableSelect
            options={collectionOptions}
            value={collection}
            onChange={(v) => setCollection(v === '' ? '' : String(v))}
            placeholder="All collections"
            dense
          />
        </FilterField>
        <FilterField label="Brand" className="sm:w-[150px]">
          <Select value={brand} onChange={(e) => setBrand(e.target.value)} className="w-full min-w-0">
            <option value="">All Brands</option>
            {(brandsQuery.data ?? []).map((b) => <option key={b} value={b}>{b}</option>)}
          </Select>
        </FilterField>
        <FilterField label="">

          <label className="flex h-6 w-full cursor-pointer items-center justify-center gap-1 rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 text-[11px] sm:h-8 sm:w-auto sm:justify-start sm:gap-1.5 sm:px-3 sm:text-sm">
            <input
              type="checkbox"
              checked={inStock}
              onChange={(e) => setInStock(e.target.checked)}
              className="h-3 w-3 sm:h-4 sm:w-4"
            />
            <span className="whitespace-nowrap">In stock<span className="hidden sm:inline"> only</span></span>
          </label>
        </FilterField>
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          title="View / edit cart"
          className="col-start-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 sm:col-start-auto sm:ml-auto sm:h-8 sm:self-end sm:gap-1.5 sm:px-3 sm:text-xs"
        >
          <ShoppingCart size={13} strokeWidth={2.25} />
          Cart
          {cart.count > 0 && (
            <span className="rounded-full bg-white/30 px-1.5 text-[10px] font-black leading-4">
              {cart.count}
            </span>
          )}
        </button>
      </FilterBar>

      <div
        className={
          // Thin scrollbar that stays out of the way: the thumb is invisible at
          // rest and fades in when you hover/scroll over the catalogue.
          'min-h-0 flex-1 overflow-y-auto pr-1 ' +
          '[&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent ' +
          '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-transparent ' +
          '[&::-webkit-scrollbar-thumb]:transition-colors ' +
          '[&:hover::-webkit-scrollbar-thumb]:bg-[var(--border-color)] ' +
          '[&::-webkit-scrollbar-thumb:hover]:bg-[var(--text-muted)]'
        }
        style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent' }}
        onMouseEnter={(e) => { e.currentTarget.style.scrollbarColor = 'var(--border-color) transparent'; }}
        onMouseLeave={(e) => { e.currentTarget.style.scrollbarColor = 'transparent transparent'; }}
      >
      {query.isFetching && rows.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center text-sm text-[var(--text-muted)]">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] p-10 text-center text-sm text-[var(--text-muted)]">
          No items match the filters
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-1.5 pb-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4 lg:grid-cols-[repeat(auto-fill,minmax(190px,1fr))]">
          {rows.map((r) => (
            <div
              key={r.id}
              className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)]"
            >
              <div className="relative aspect-[4/3] w-full flex-shrink-0 overflow-hidden bg-white/[0.03]">
                {r.imageUrl ? (
                  <button
                    type="button"
                    onClick={() => setPreview({ url: r.imageUrl!, label: r.name })}
                    title="Click to preview"
                    className="absolute inset-0 h-full w-full"
                  >
                    <img src={r.imageUrl} alt={r.name} className="h-full w-full object-cover transition duration-200 hover:scale-105" />
                  </button>
                ) : (
                  <div className="flex h-full items-center justify-center text-[9px] text-[var(--text-muted)] sm:text-xs">
                    No image
                  </div>
                )}
                {/* Compact status pill overlay */}
                <span
                  className={
                    'absolute right-1 top-1 rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase leading-none sm:text-[10px] ' +
                    (r.stockStatus === 'GREEN'
                      ? 'bg-success/90 text-white'
                      : r.stockStatus === 'AMBER'
                      ? 'bg-warning/90 text-white'
                      : 'bg-danger/90 text-white')
                  }
                >
                  {STOCK_LABEL[r.stockStatus]}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col p-1.5 sm:gap-1 sm:p-3">
                <div
                  className="break-words text-[11px] font-bold leading-tight text-[var(--text-primary)] sm:text-[13px]"
                  title={r.name}
                >
                  {wrappableName(r.name)}
                </div>
                <div className="flex items-baseline justify-between gap-1 sm:hidden">
                  <div className="text-[13px] font-black leading-none text-accent">
                    {formatINRSymbol(r.customerPrice)}
                  </div>
                  <div
                    className="flex-shrink-0 text-[9px] font-bold text-success"
                    title={`Available ${r.availableQty} = Stock ${r.currentStock} − Reserved ${r.reservedQty}`}
                  >
                    Avail {r.availableQty} {r.unit}
                  </div>
                </div>
                {/* Desktop / tablet layout */}
                <div className="hidden sm:flex sm:flex-col sm:gap-1">
                  <div className="flex items-baseline justify-between gap-1">
                    <div className="min-w-0 truncate text-[11px] text-[var(--text-muted)]">
                      {r.category}
                    </div>
                    <div
                      className="flex-shrink-0 text-right leading-tight"
                      title={`Available = Stock ${r.currentStock} − Reserved ${r.reservedQty}`}
                    >
                      <div className="text-[11px] font-bold text-[var(--text-secondary)]">{r.currentStock} {r.unit}</div>
                      <div className="text-[10px] font-bold text-success">Avail {r.availableQty}</div>
                    </div>
                  </div>
                  <div className="mt-auto flex items-end justify-between gap-1 pt-1">
                    <div className="min-w-0">
                      <div className="text-lg font-black leading-none text-accent">
                        {formatINRSymbol(r.customerPrice)}
                      </div>
                      {customerType && customerType !== 'Retailer' ? (
                        <div className="mt-0.5 text-[10px] text-[var(--text-muted)] line-through">
                          {formatINRSymbol(r.mrp)}
                        </div>
                      ) : (
                        <div className="mt-0.5 text-[10px] font-bold uppercase text-[var(--text-muted)]">
                          {customerType || 'MRP'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setPopupItem(r)}
                title={r.stockStatus === 'RED' ? 'Out of stock — can still be added for enquiry' : 'Add to cart'}
                className="mx-1.5 mb-1.5 inline-flex items-center justify-center gap-1 rounded-md bg-accent px-1 py-1 text-[10px] font-bold text-white transition hover:brightness-110 sm:mx-2 sm:mb-2 sm:px-2 sm:py-1.5 sm:text-xs"
              >
                <ShoppingCart size={10} className="sm:hidden" />
                <ShoppingCart size={12} className="hidden sm:inline" />
                Add
                <span className="hidden sm:inline">to Cart</span>
              </button>
            </div>
          ))}
        </div>
      )}
      {!isSearching && !inStock && rows.length >= limit && (
        <div className="flex justify-center py-4">
          <button
            type="button"
            onClick={() => setLimit((n) => n + PAGE_SIZE)}
            disabled={query.isFetching}
            title={`${rows.length} items shown`}
            className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] px-5 py-2 text-xs font-bold text-[var(--text-primary)] transition hover:bg-[var(--bg-soft)] disabled:opacity-60"
          >
            {query.isFetching ? 'Loading…' : <>Load more <ChevronDown size={14} /></>}
          </button>
        </div>
      )}
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setPreview(null)}
        >
          <div className="relative max-h-[90vh] max-w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreview(null)}
              className="absolute -right-3 -top-3 rounded-full bg-[var(--bg-primary)] p-1.5 text-[var(--text-primary)] shadow-lg ring-1 ring-[var(--border-color)] hover:text-danger"
              title="Close"
            >
              <X size={18} />
            </button>
            <img
              src={preview.url}
              alt={preview.label}
              className="max-h-[80vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
            />
            <div className="mt-2 text-center text-xs font-semibold text-white/90">{preview.label}</div>
          </div>
        </div>
      )}

      <AddToCartPopup
        open={!!popupItem}
        onClose={() => setPopupItem(null)}
        item={popupItem}
      />
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
      />
    </div>
  );
}
